import { useState, useEffect } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Menu, X, ArrowLeft, ShieldAlert } from 'lucide-react';
import TrainingAdminDashboard from '@/components/training-admin/DashboardSection';
import CoursesSection from '@/components/training-admin/CoursesSection';
import CourseBuilderSection from '@/components/training-admin/CourseBuilderSection';
import ContentLibrarySection from '@/components/training-admin/ContentLibrarySection';
import UploadImportSection from '@/components/training-admin/UploadImportSection';
import CategoryManagerSection from '@/components/training-admin/CategoryManagerSection';
import PolicyAdminSection from '@/components/training-admin/PolicyAdminSection';
import SOPAdminSection from '@/components/training-admin/SOPAdminSection';
import HandbookAdminSection from '@/components/training-admin/HandbookAdminSection';
import MissingContentSection from '@/components/training-admin/MissingContentSection';
import ModulesAdminSection from '@/components/training-admin/ModulesAdminSection';
import EnrollmentsSection from '@/components/training-admin/EnrollmentsSection';
import EmployeeProgressSection from '@/components/training-admin/EmployeeProgressSection';
import CertificatesSection from '@/components/training-admin/CertificatesSection';
import AuditHistorySection from '@/components/training-admin/AuditHistorySection';
import PracticalAssessmentsSection from '@/components/training-admin/PracticalAssessmentsSection';

type Section = 'dashboard' | 'courses' | 'course-builder' | 'modules' | 'questions' | 'assessments'
  | 'library' | 'imports' | 'categories'
  | 'policies' | 'sops' | 'handbook'
  | 'enrollments' | 'progress' | 'certificates' | 'missing-content' | 'reports' | 'audit-log' | 'settings';

const NAV_GROUPS = [
  { label: 'Overview', items: [{ key: 'dashboard' as Section, label: 'Dashboard' }] },
  { label: 'Course Management', items: [
    { key: 'courses' as Section, label: 'Courses' },
    { key: 'modules' as Section, label: 'Modules & Lessons' },
    { key: 'questions' as Section, label: 'Question Banks' },
    { key: 'assessments' as Section, label: 'Practical Assessments' },
  ] },
  { label: 'Content', items: [
    { key: 'library' as Section, label: 'Content Library' },
    { key: 'imports' as Section, label: 'Upload & Import' },
    { key: 'categories' as Section, label: 'Categories' },
  ] },
  { label: 'Controlled Documents', items: [
    { key: 'policies' as Section, label: 'Policies' },
    { key: 'sops' as Section, label: 'SOPs' },
    { key: 'handbook' as Section, label: 'Handbook' },
  ] },
  { label: 'Operations', items: [
    { key: 'enrollments' as Section, label: 'Enrolments' },
    { key: 'progress' as Section, label: 'Employee Progress' },
    { key: 'certificates' as Section, label: 'Certificates' },
    { key: 'missing-content' as Section, label: 'Missing Content' },
    { key: 'reports' as Section, label: 'Reports' },
    { key: 'audit-log' as Section, label: 'Audit History' },
    { key: 'settings' as Section, label: 'Settings' },
  ] },
];

function PermissionDenied() {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
      <ShieldAlert className="w-16 h-16 text-destructive mb-4" />
      <h2 className="text-xl font-bold text-foreground mb-2">Permission Denied</h2>
      <p className="text-sm text-muted-foreground mb-4">
        You do not have permission to access Training Administration.<br />
        This area is restricted to Admin, Owner and Manager roles.
      </p>
      <Link to="/train" className="text-sm text-primary hover:underline">← Back to My Training</Link>
    </div>
  );
}

function PlaceholderSection({ title, description }: { title: string; description: string }) {
  return (
    <div className="py-12 text-center">
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">{description}</p>
    </div>
  );
}

export default function TrainingAdmin() {
  const { user, isLoading, loginWithRedirect } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const currentSection = (searchParams.get('section') as Section) || 'dashboard';
  const courseId = searchParams.get('courseId') || undefined;

  useEffect(() => {
    if (!isLoading && !user) loginWithRedirect({ redirectUrl: window.location.href });
  }, [isLoading, user, loginWithRedirect]);

  if (isLoading || !user) return null;

  if (!['Admin', 'Owner', 'Manager', 'Training Admin', 'Course Admin', 'Policy Admin'].includes(user.role || '')) {
    return <PermissionDenied />;
  }

  const policyOnly=user.role==='Policy Admin';
  const courseOnly=user.role==='Course Admin';
  const visibleGroups=policyOnly?NAV_GROUPS.filter(group=>group.label==='Controlled Documents'):courseOnly?NAV_GROUPS.filter(group=>group.label!=='Controlled Documents'):NAV_GROUPS;
  const allowedSections=new Set(visibleGroups.flatMap(group=>group.items.map(item=>item.key)));
  const effectiveSection=allowedSections.has(currentSection)?currentSection:(policyOnly?'policies':'dashboard');

  const setSection = (s: Section) => {
    setSearchParams({ section: s });
    setMobileMenuOpen(false);
  };

  const openCourseBuilder = (id: string) => {
    setSearchParams({ section: 'course-builder', courseId: id });
  };

  const sectionLabel = NAV_GROUPS.flatMap(g => g.items).find(i => i.key === effectiveSection)?.label || 'Dashboard';

  return (
    <div className="w-full min-h-[calc(100vh-3.5rem)]">
      {/* Breadcrumb header */}
      <div className="border-b border-border bg-card px-4 md:px-8 py-3 flex items-center gap-3">
        <button className="md:hidden text-muted-foreground" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <Link to="/train" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <span className="font-bold text-foreground text-sm">Training Administration</span>
        <span className="text-muted-foreground text-sm">/</span>
        <span className="text-sm text-muted-foreground">{sectionLabel}</span>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${mobileMenuOpen ? 'fixed inset-0 top-[6.5rem] z-40 block' : 'hidden'} md:relative md:block md:top-auto md:inset-auto md:z-auto w-full md:w-56 shrink-0 border-r border-border bg-card p-3 min-h-[calc(100vh-7rem)] md:sticky md:top-14 overflow-y-auto`}>
          {visibleGroups.map(group => (
            <div key={group.label} className="mb-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-1">{group.label}</p>
              {group.items.map(item => (
                <button
                  key={item.key}
                  onClick={() => setSection(item.key)}
                  className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${
                    effectiveSection === item.key
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </aside>

        {/* Main content */}
        <main className="flex-1 p-4 md:p-6 min-w-0">
          {effectiveSection === 'dashboard' && <TrainingAdminDashboard onNavigate={setSection} />}
          {effectiveSection === 'courses' && <CoursesSection onEditCourse={openCourseBuilder} />}
          {effectiveSection === 'course-builder' && courseId && <CourseBuilderSection courseId={courseId} onBack={() => setSection('courses')} />}
          {effectiveSection === 'modules' && <ModulesAdminSection />}
          {effectiveSection === 'library' && <ContentLibrarySection />}
          {effectiveSection === 'imports' && <UploadImportSection />}
          {effectiveSection === 'categories' && <CategoryManagerSection />}
          {effectiveSection === 'policies' && <PolicyAdminSection />}
          {effectiveSection === 'sops' && <SOPAdminSection />}
          {effectiveSection === 'handbook' && <HandbookAdminSection />}
          {effectiveSection === 'missing-content' && <MissingContentSection />}
          {effectiveSection === 'questions' && <PlaceholderSection title="Question Banks" description="Manage quiz questions, set pass marks, flag critical questions and organise by module. Question Banks connect to Course Modules for learner quizzes." />}
          {effectiveSection === 'assessments' && <PracticalAssessmentsSection />}
          {effectiveSection === 'enrollments' && <EnrollmentsSection />}
          {effectiveSection === 'progress' && <EmployeeProgressSection />}
          {effectiveSection === 'certificates' && <CertificatesSection />}
          {effectiveSection === 'reports' && <PlaceholderSection title="Reports" description="Training completion reports, overdue training, assessment pass rates, and compliance status across all employees and courses." />}
          {effectiveSection === 'audit-log' && <AuditHistorySection />}
          {effectiveSection === 'settings' && <PlaceholderSection title="Settings" description="Configure training system settings — default pass marks, assessment rules, certificate templates, notification preferences and integration options." />}
        </main>
      </div>
    </div>
  );
}
